// One picked or shared file into a chat: photos resized (D135) with a
// preview, videos with their poster frame, anything else as a file. The
// upload itself, sealed when the device has its key (M28), with retries and
// progress, is lib/attachments.ts. Used by the composer and by Share to
// Twinden (D181), so both send the same way.
import {
  prepareImage,
  sendAlbum,
  sendAttachment,
  type AlbumItemInput,
} from './attachments';
import { chunkForAlbums } from './albums';
import { resizePhoto } from './photos';
import { shareKind } from './shares';
import { readVideoMeta } from '../components/chat/messageUtils';
import type { SentMessage } from '../hooks/useConversationMessages';
import type { PhotoQuality } from '../store/alerts';

export async function sendFile(
  conversationId: string,
  original: File,
  options: {
    photoQuality: PhotoQuality;
    onSent: (message: SentMessage) => void;
    /** What it was picked as; otherwise told by its type. */
    as?: 'image' | 'video' | 'file';
    /** Quoted reply (D20). */
    replyToId?: string;
    /** Text sent after it once it's confirmed (D219). */
    caption?: string;
  }
): Promise<string> {
  const kind = options.as ?? shareKind(original);
  const { onSent, replyToId, caption } = options;

  if (kind === 'image' || kind === 'gif') {
    // Sent at most 1600px unless this device asks for originals (D135, D136).
    const file =
      options.photoQuality === 'original'
        ? original
        : await resizePhoto(original);
    const { width, height, thumbnail } = await prepareImage(file);
    return sendAttachment({
      conversationId,
      replyToId,
      caption,
      onSent,
      kind: file.type === 'image/gif' ? 'gif' : 'image',
      file,
      fileName: file.name,
      meta: { width, height },
      thumbnail,
      previewUrl: URL.createObjectURL(thumbnail ?? file),
    });
  }

  if (kind === 'video') {
    const meta = await readVideoMeta(original);
    return sendAttachment({
      conversationId,
      replyToId,
      caption,
      onSent,
      kind: 'video',
      file: original,
      fileName: original.name,
      meta: { width: meta.width, height: meta.height, duration: meta.duration },
      thumbnail: meta.thumbnail,
      previewUrl: meta.thumbnail ? URL.createObjectURL(meta.thumbnail) : null,
    });
  }

  return sendAttachment({
    conversationId,
    replyToId,
    caption,
    onSent,
    kind: 'file',
    file: original,
    fileName: original.name,
  });
}

/**
 * One picked photo or video, ready to go into an album (M60, D219): resized
 * and given a preview exactly as a single one would be.
 */
export async function prepareAlbumItem(
  original: File,
  photoQuality: PhotoQuality
): Promise<AlbumItemInput> {
  if (shareKind(original) === 'video') {
    const meta = await readVideoMeta(original);
    return {
      kind: 'video',
      file: original,
      fileName: original.name,
      meta: { width: meta.width, height: meta.height, duration: meta.duration },
      thumbnail: meta.thumbnail,
      previewUrl: meta.thumbnail ? URL.createObjectURL(meta.thumbnail) : null,
    };
  }
  const file =
    photoQuality === 'original' ? original : await resizePhoto(original);
  const { width, height, thumbnail } = await prepareImage(file);
  return {
    kind: file.type === 'image/gif' ? 'gif' : 'image',
    file,
    fileName: file.name,
    meta: { width, height },
    thumbnail,
    previewUrl: URL.createObjectURL(thumbnail ?? file),
  };
}

/** Runs at most `limit` of the tasks at once, in the order they're asked. */
export function limitConcurrency(limit: number) {
  let active = 0;
  const waiting: (() => void)[] = [];
  return async <T>(task: () => Promise<T>): Promise<T> => {
    if (active >= limit) {
      await new Promise<void>((resolve) => waiting.push(resolve));
    }
    active += 1;
    try {
      return await task();
    } finally {
      active -= 1;
      waiting.shift()?.();
    }
  };
}

// Resizing several large photos at once can run a phone out of memory.
const PREPARE_AT_ONCE = 2;

/**
 * Picked photos and videos as albums of up to 10, in order (D219). Each
 * album's bubble shows at once; its items are resized at most two at a
 * time and each goes up as soon as it's ready. The caption and reply go
 * with the first. A group of one (one picked, or the eleventh of eleven)
 * goes as a plain photo or video; if it's the first, its caption follows it
 * as a text message replying to the same message.
 */
export async function sendAlbums(
  conversationId: string,
  files: File[],
  options: {
    photoQuality: PhotoQuality;
    onSent: (message: SentMessage) => void;
    caption?: string;
    replyToId?: string;
    /** For tests: how an item is prepared. */
    prepare?: (file: File) => Promise<AlbumItemInput>;
  }
): Promise<void> {
  const limit = limitConcurrency(PREPARE_AT_ONCE);
  const prepare =
    options.prepare ??
    ((file: File) => prepareAlbumItem(file, options.photoQuality));
  let first = true;
  for (const group of chunkForAlbums(files)) {
    const caption = first ? options.caption : undefined;
    const replyToId = first ? options.replyToId : undefined;
    first = false;
    if (group.length === 1) {
      // The caption rides with the upload: sent after the photo, so it
      // reads as its caption, and given back if the photo is cancelled.
      await sendFile(conversationId, group[0]!, {
        photoQuality: options.photoQuality,
        replyToId,
        caption,
        onSent: options.onSent,
      });
      continue;
    }
    // Nothing is resized until the album's bubble is on screen.
    let shown!: () => void;
    const bubble = new Promise<void>((resolve) => (shown = resolve));
    sendAlbum({
      conversationId,
      kinds: group.map((file) =>
        shareKind(file) === 'video' ? 'video' : 'image'
      ),
      items: group.map(
        (file) => () => bubble.then(() => limit(() => prepare(file)))
      ),
      caption,
      replyToId,
      onSent: options.onSent,
    });
    shown();
  }
}
